import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-prek-toal-homestay.jpg";

const itinerary = [
  {
    time: "Day 1 · 2:00 PM",
    title: "Hotel Pick-up & Transfer to Tonle Sap",
    body:
      "Pick-up from your hotel in Siem Reap and private drive to the boat pier on Tonle Sap Lake to begin your journey through the floating villages.",
  },
  {
    time: "Day 1 · Boat Journey",
    title: "Through the Floating Villages",
    body:
      "Cruise across Tonle Sap, visit floating houses and fishing communities, and learn about daily life on the great lake.",
  },
  {
    time: "Day 1 · Afternoon",
    title: "Arrival at Prek Toal Village",
    body:
      "Arrive at the Prek Toal community area, check-in at your floating homestay or eco-lodge, and rest while soaking in the lake environment.",
  },
  {
    time: "Day 1 · Sunset",
    title: "Flooded Forest & Sunset Birdwatching",
    body:
      "Boat ride around the seasonally flooded forest with late-afternoon birdwatching, then enjoy a spectacular sunset over Tonle Sap Lake.",
  },
  {
    time: "Day 1 · Evening",
    title: "Community Dinner & Overnight Stay",
    body:
      "Share dinner with a local family or community host and overnight in the floating village.",
  },
  {
    time: "Day 2 · Sunrise",
    title: "Core Bird Sanctuary Boat Trip",
    body:
      "Sunrise boat trip into the protected core of Prek Toal quiet navigation through nesting areas, observation from bird towers, and photography of rare and endangered species.",
  },
  {
    time: "Day 2 · Morning",
    title: "Bird Species & Conservation",
    body:
      "Spot Painted Stork, Spot-billed Pelican, Greater Adjutant, Asian Openbill, Black-headed Ibis, Grey-headed Fish Eagle and more, while learning about ongoing conservation efforts.",
  },
  {
    time: "Day 2 · 2:00–3:00 PM",
    title: "Breakfast, Lunch & Return to Siem Reap",
    body:
      "Breakfast at the village, boat back through Tonle Sap with lunch en route, and private transfer back to your hotel in Siem Reap (arrival around 2:00–3:00 PM).",
  },
];

const includes = [
  "English-speaking bird guide",
  "Private boat & transportation",
  "Floating homestay accommodation (1 night)",
  "Meals: 1 dinner, 1 breakfast & 1 lunch on Day 2",
  "Drinking water",
  "Hotel pick-up & drop-off in Siem Reap",
];

const excludes = [
  "Personal expenses",
  "Optional donations to conservation projects",
  "Travel insurance",
  "Tips and gratuities",
];

const essentials = [
  "Hat, sunglasses & sunscreen",
  "Insect repellent",
  "Binoculars (a few pairs available to share)",
  "Light, breathable clothing & a change of clothes",
];

const pricing = [
  { group: "1–2 people", price: "$220 – $260" },
  { group: "3–5 people", price: "$280 – $340" },
  { group: "6–10 people", price: "$360 – $420" },
];

const TourPrekToalHomestay = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Prek Toal Bird Sanctuary floating homestay — Cambodia birdwatching tour from Siem Reap with French-speaking guide"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 2 Days / 1 Night Floating Home Stay</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Prek Toal <em className="font-light">Floating Village</em> & Bird Sanctuary.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              Sleep in a floating home on Tonle Sap, glide through the flooded forest at sunset, and wake at sunrise for one of Southeast Asia's most spectacular birdwatching experiences.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days / 1 Night</span>
              <span>Group · 1–10</span>
              <span>Pickup · 2:00 PM Day 1</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            A Ramsar wetland, a floating home, and the dawn chorus.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Experience the unique ecosystem of Prek Toal Bird Sanctuary, one of the most important waterbird breeding sites in Southeast Asia. Stay overnight in a floating village on Tonle Sap Lake, explore flooded forests, and enjoy incredible birdwatching with expert local guidance.
            </p>
            <p>
              Day 1 takes you across the lake into the village visit fishing communities, settle into your floating homestay, and head out at sunset for your first taste of the flooded forest and its birdlife.
            </p>
            <p>
              Day 2 begins before sunrise, when the sanctuary is at its most alive. Travel quietly into protected nesting areas to observe rare species Painted Stork, Spot-billed Pelican, Greater Adjutant — before a leisurely return to Siem Reap.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Two days, one night, eight stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={387} tourTitle="2 Days / 1 Night Prek Toal Floating Home Stay & Bird Sanctuary" pricingTiers={[{ maxPax: 1, price: 387 }, { maxPax: 2, price: 412 }, { maxPax: 4, price: 467 }, { maxPax: 5, price: 467 }, { maxPax: 6, price: 497 }, { maxPax: 7, price: 545 }, { maxPax: 8, price: 587 }, { maxPax: 9, price: 628 }, { maxPax: 10, price: 665 }]} />
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need on the lake.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Physical Effort</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                No long uphill hikes, but you should be fit enough to walk up to 1 km in hot conditions on flat or uneven ground. Within the capability of most healthy travellers comfortable with tropical climates.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather is expected throughout. Occasional showers are possible. Midday is hot; wetland sites can be humid. The pace is designed so you avoid birding through the worst of the heat.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine is similar to most Southeast Asian cooking but rather less spicy. Lodge staff prepare a wide variety of good local food, with fresh bread widely available and a surprising range of local brews to try.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Bring With You</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourPrekToalHomestay;
